/**
 * StatusPanel – compact readiness summary for admin place editor.
 * Lives in right column on desktop. Each row can scroll to its section.
 */
import { CheckCircle2, AlertCircle, Image, MapPin, Leaf, Sparkles, FileText } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Badge } from '@/components/ui/badge';

interface StatusRow {
  id: string;
  label: string;
  icon: React.ReactNode;
  value: string;
  ok: boolean;
}

interface StatusPanelProps {
  placeName: string;
  status: 'draft' | 'public';
  descriptionLength: number;
  vibeLength: number;
  charTagCount: number;
  totalImages: number;
  hasCover: boolean;
  hasLocation: boolean;
  hasCoords: boolean;
  hasContact: boolean;
  seasonsFilled: number;
  isNew: boolean;
}

function scrollToSection(id: string) {
  const el = document.getElementById(id);
  if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
}

export function StatusPanel({
  placeName,
  status,
  descriptionLength,
  vibeLength,
  charTagCount,
  totalImages,
  hasCover,
  hasLocation,
  hasCoords,
  hasContact,
  seasonsFilled,
  isNew,
}: StatusPanelProps) {
  const rows: StatusRow[] = [
    {
      id: 'section-grund',
      label: 'Grund',
      icon: <FileText className="h-3.5 w-3.5" />,
      value: descriptionLength > 0 ? `${descriptionLength} tecken` : 'Saknas',
      ok: descriptionLength >= 40,
    },
    {
      id: 'section-placecore',
      label: 'PlaceCore',
      icon: <Sparkles className="h-3.5 w-3.5" />,
      value: `${vibeLength} tecken · ${charTagCount} taggar`,
      ok: vibeLength >= 80 && charTagCount >= 1,
    },
    {
      id: 'section-bilder',
      label: 'Bilder',
      icon: <Image className="h-3.5 w-3.5" />,
      value: `${totalImages} bild${totalImages !== 1 ? 'er' : ''} · omslag: ${hasCover ? 'ja' : 'nej'}`,
      ok: totalImages > 0 && hasCover,
    },
    {
      id: 'section-praktiskt',
      label: 'Praktisk info',
      icon: <MapPin className="h-3.5 w-3.5" />,
      value: [
        hasLocation ? 'plats ✓' : 'plats ✗',
        hasCoords ? 'coords ✓' : 'coords ✗',
        hasContact ? 'kontakt ✓' : '',
      ].filter(Boolean).join(' · '),
      ok: hasLocation && hasCoords,
    },
  ];

  if (!isNew) {
    rows.push({
      id: 'section-arstider',
      label: 'Årstider',
      icon: <Leaf className="h-3.5 w-3.5" />,
      value: `${seasonsFilled} av 4 ifyllda`,
      ok: seasonsFilled > 0,
    });
  }

  const allOk = rows.every((r) => r.ok);

  return (
    <div className="rounded-lg border bg-card text-card-foreground">
      <div className="px-4 py-3 border-b">
        <div className="flex items-center justify-between">
          <h3 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
            Status & nästa steg
          </h3>
          <Badge
            variant={status === 'public' ? 'default' : 'secondary'}
            className={cn(
              'text-[10px] px-1.5 py-0',
              status === 'public'
                ? 'bg-primary/10 text-primary border-primary/20'
                : 'bg-muted text-muted-foreground',
            )}
          >
            {status === 'public' ? 'Publicerad' : 'Utkast'}
          </Badge>
        </div>
        {!allOk && (
          <p className="text-[11px] text-muted-foreground mt-1">
            Fyll i saknade fält för att stärka platsprofilen.
          </p>
        )}
      </div>

      <div className="divide-y">
        {rows.map((row) => (
          <button
            key={row.id}
            type="button"
            onClick={() => scrollToSection(row.id)}
            className="w-full flex items-center gap-2.5 px-4 py-2.5 text-left hover:bg-muted/30 transition-colors"
          >
            <span className={cn('shrink-0', row.ok ? 'text-primary' : 'text-muted-foreground')}>
              {row.ok ? <CheckCircle2 className="h-3.5 w-3.5" /> : <AlertCircle className="h-3.5 w-3.5" />}
            </span>
            <span className="flex-1 min-w-0">
              <span className="text-xs font-medium text-foreground">{row.label}</span>
              <span className="block text-[11px] text-muted-foreground truncate">{row.value}</span>
            </span>
            <span className="shrink-0 text-muted-foreground">{row.icon}</span>
          </button>
        ))}
      </div>
    </div>
  );
}
